import type { EmailTemplate, EmailTemplateActor, EmailTemplateCategory } from '@/types/email-template'
import { FAN_TEMPLATES, type FanTemplateId } from '@/types/fan-email'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

const messagingApiUrl = import.meta.env.VITE_MESSAGING_API_URL

// Create a separate axios instance for the Messaging API
export const messagingHttp = axios.create({
  baseURL: messagingApiUrl,
  timeout: 15000,
  timeoutErrorMessage: 'Request timed out'
})

messagingHttp.interceptors.request.use(async req => {
  const token = await window.Clerk.session?.getToken()
  if (!token) {
    window.Clerk.session?.remove()
    return Promise.reject(new Error('No authentication token available'))
  }

  req.headers.Authorization = `Bearer ${token}`

  return req
})

// ============================================================================
// API Functions
// ============================================================================

/**
 * Render a FAN email template with the given data
 */
async function renderFanEmailTemplate(
  templateId: FanTemplateId,
  vendorHash: string,
  data: Record<string, unknown>
): Promise<string> {
  const response = await messagingHttp.post<string>(
    `/api/email/${templateId}/render`,
    { vendorHash, data },
    { responseType: 'text' }
  )
  return response.data
}

/**
 * Send an email template
 */
async function sendEmailTemplate(params: {
  templateId: FanTemplateId
  vendorHash: string
  data: Record<string, unknown>
  to: string | string[]
  from: string
  subject?: string
  bcc?: string | string[]
  replyTo?: string
  timeline?: {
    orderHash?: string
    itemHash?: string
    patientHash?: string
    userHash?: string
  }
}): Promise<{ success: boolean }> {
  const { templateId, to, bcc, ...rest } = params
  // Messaging API expects arrays for to/bcc
  const body = {
    ...rest,
    to: Array.isArray(to) ? to : [to],
    ...(bcc && { bcc: Array.isArray(bcc) ? bcc : [bcc] })
  }
  const response = await messagingHttp.post(`/api/email/${templateId}`, body)
  return response.data
}

interface RawPresetData {
  condition?: string
  treatments?: string[]
  content?: string
  category?: EmailTemplateCategory
}

interface RawPreset {
  id: string
  templateId: string
  name: string
  data: RawPresetData
  updatedAt: string
  updatedBy: EmailTemplateActor
}

const toEmailTemplate = (preset: RawPreset): EmailTemplate => ({
  id: preset.id,
  templateId: preset.templateId,
  name: preset.name,
  condition: preset.data.condition ?? '',
  treatments: preset.data.treatments ?? [],
  content: preset.data.content ?? '',
  category: preset.data.category ?? 'fan',
  updatedAt: preset.updatedAt,
  updatedBy: preset.updatedBy
})

/**
 * The FAN email templates (cs-on-hold, cs-refund, cs-follow-up) are the
 * "container" templates that own the presets shown in SendFanEmailDialog.
 */
async function fetchEmailTemplates(): Promise<EmailTemplate[]> {
  const lists = await Promise.all(
    FAN_TEMPLATES.map(async template => {
      const response = await messagingHttp.get<RawPreset[]>('/api/presets', { params: { template: template.id } })
      return response.data
    })
  )
  return lists.flat().map(toEmailTemplate)
}

interface EmailTemplateFields {
  name: string
  condition: string
  treatments: string[]
  content: string
  category: EmailTemplateCategory
}

export interface CreateEmailTemplateParams extends EmailTemplateFields {
  templateId: string
}

async function createEmailTemplate(params: CreateEmailTemplateParams): Promise<EmailTemplate> {
  const { templateId, name, ...data } = params
  const response = await messagingHttp.post<RawPreset>('/api/presets', { templateId, name, data })
  return toEmailTemplate(response.data)
}

export interface UpdateEmailTemplateParams extends EmailTemplateFields {
  id: string
}

async function updateEmailTemplate(params: UpdateEmailTemplateParams): Promise<EmailTemplate> {
  const { id, name, ...data } = params
  const response = await messagingHttp.put<RawPreset>(`/api/presets/${id}`, { name, data })
  return toEmailTemplate(response.data)
}

async function deleteEmailTemplate(id: string): Promise<void> {
  await messagingHttp.delete(`/api/presets/${id}`)
}

// ============================================================================
// Hooks
// ============================================================================

const EMAIL_TEMPLATES_QUERY_KEY = ['messaging', 'email-templates']

export function useEmailTemplatesQuery(enabled = true) {
  return useQuery({
    queryKey: EMAIL_TEMPLATES_QUERY_KEY,
    queryFn: fetchEmailTemplates,
    enabled,
    staleTime: 5 * 60 * 1000
  })
}

/**
 * Hook for creating a new email template preset
 */
export function useCreateEmailTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createEmailTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMAIL_TEMPLATES_QUERY_KEY })
  })
}

/**
 * Hook for updating an existing email template preset
 */
export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateEmailTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMAIL_TEMPLATES_QUERY_KEY })
  })
}

/**
 * Hook for deleting an email template preset
 */
export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteEmailTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMAIL_TEMPLATES_QUERY_KEY })
  })
}

export type RenderPreviewParams = {
  templateId: FanTemplateId
  vendorHash: string
  data: Record<string, unknown>
}

/**
 * Hook for rendering FAN email preview
 * Uses mutation since preview is triggered manually (not auto-fetched)
 */
export function useFanEmailPreview() {
  return useMutation({
    mutationFn: (params: RenderPreviewParams) =>
      renderFanEmailTemplate(params.templateId, params.vendorHash, params.data)
  })
}

/**
 * Hook for sending email templates through the Messaging service
 */
export function useSendEmailTemplate() {
  return useMutation({
    mutationFn: sendEmailTemplate
  })
}
